/**
 * AnnotationEditor
 * Floating Card zum Erstellen/Bearbeiten einer Annotation.
 */

import React, { useEffect, useState, useRef, useCallback } from 'react'
import { useAnnotationStore, selectActiveAnnotation } from '../../store/annotations'
import {
  Annotation,
  AnnotationType,
  ANNOTATION_TYPE_LABELS,
  ANNOTATION_TYPE_COLORS,
  EMPTY_TIPTAP_DOC,
} from '../../types/annotations'
import { captureCurrentCamera } from '../../hooks/useCameraNavigation'
import { captureAndStoreAnnotationThumbnail } from '../../lib/thumbnailService'
import { C } from '../../styles/theme'
import {
  PaperlessDocument,
  fetchDocuments,
  getDocumentThumbnailUrl,
  getDocumentPreviewUrl,
} from '../../lib/paperless/api'

// ─── Type selector items ──────────────────────────────────────────────────────

const ANNOTATION_TYPES: AnnotationType[] = [
  'position_marker', 'construction_detail', 'material_property',
  'diagnostics', 'pollutant', 'core_drill', 'measurement',
]

/** Universal SVG marker icon — filled circle with outer ring, coloured per type */
function TypeMarker({ color, size = 16 }: { color: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="9" fill="#ffffff" stroke={color} strokeWidth="2.5" />
      <circle cx="12" cy="12" r="4" fill={color} />
    </svg>
  )
}


// ─── Props ────────────────────────────────────────────────────────────────────

interface AnnotationEditorProps {
  viewerRef: React.RefObject<any>
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Persist last drag position across editor open/close cycles */
let _lastDragPos: { x: number; y: number } | null = null

// ─── Shared button styles ─────────────────────────────────────────────────────

const actionBtnBase: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '1.875em',   /* 30px at 16px */
  height: '1.875em',
  padding: '0',
  borderRadius: '0.375em', /* 6px at 16px */
  border: `1px solid ${C.border}`,
  background: '#ffffff',
  color: C.textSecondary,
  cursor: 'pointer',
  transition: `all 0.15s ease`,
}

const dangerBtnBase: React.CSSProperties = {
  ...actionBtnBase,
  color: C.danger,
  borderColor: C.dangerBorder,
}

// ─── Component ────────────────────────────────────────────────────────────────

export function AnnotationEditor({ viewerRef }: AnnotationEditorProps) {
  const annotation = useAnnotationStore(selectActiveAnnotation)
  const mode = useAnnotationStore(s => s.editor.mode)
  const updateAnnotation = useAnnotationStore(s => s.updateAnnotation)
  const deleteAnnotation = useAnnotationStore(s => s.deleteAnnotation)
  const duplicateAnnotation = useAnnotationStore(s => s.duplicateAnnotation)
  const startRelocating = useAnnotationStore(s => s.startRelocatingAnnotation)
  const openAnnotationEditor = useAnnotationStore(s => s.openAnnotationEditor)
  const closeEditor = useAnnotationStore(s => s.closeEditor)

  const [title, setTitle] = useState('')
  const [tooltip, setTooltip] = useState('')
  const [type, setType] = useState<AnnotationType>('position_marker')
  const [showPopup, setShowPopup] = useState(true)
  const [showTypeDropdown, setShowTypeDropdown] = useState(false)
  const [showActions, setShowActions] = useState(false)
  const [linkedDocIds, setLinkedDocIds] = useState<number[]>([])
  const [linkedDocs, setLinkedDocs] = useState<PaperlessDocument[]>([])
  const [showDocPicker, setShowDocPicker] = useState(false)
  const [pickerDocs, setPickerDocs] = useState<PaperlessDocument[]>([])
  const [pickerQuery, setPickerQuery] = useState('')
  const [pickerLoading, setPickerLoading] = useState(false)
  const typeDropdownRef = useRef<HTMLDivElement>(null)
  const actionsRef = useRef<HTMLDivElement>(null)
  const docPickerRef = useRef<HTMLDivElement>(null)

  // ── Drag logic ───────────────────────────────────────────────
  const dialogRef = useRef<HTMLDivElement>(null)
  const [dragPos, setDragPos] = useState<{ x: number; y: number } | null>(_lastDragPos)
  const draggingRef = useRef(false)
  const dragOffsetRef = useRef({ x: 0, y: 0 })

  const handleDragStart = (e: React.MouseEvent) => {
    // Don't drag if clicking the close button
    if ((e.target as HTMLElement).closest('[data-element="btn-close"]')) return
    const el = dialogRef.current
    if (!el) return
    draggingRef.current = true
    const rect = el.getBoundingClientRect()
    dragOffsetRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top }
    e.preventDefault()
    e.stopPropagation()
  }

  useEffect(() => {
    // Use capture phase to intercept before Potree's OrbitControls
    const onMove = (e: MouseEvent) => {
      if (!draggingRef.current) return
      e.stopPropagation()
      e.stopImmediatePropagation()
      e.preventDefault()
      const pos = {
        x: e.clientX - dragOffsetRef.current.x,
        y: e.clientY - dragOffsetRef.current.y,
      }
      setDragPos(pos)
      _lastDragPos = pos  // persist across open/close
    }
    const onUp = (e: MouseEvent) => {
      if (draggingRef.current) {
        e.stopPropagation()
        e.stopImmediatePropagation()
      }
      draggingRef.current = false
    }
    window.addEventListener('mousemove', onMove, true)  // capture phase
    window.addEventListener('mouseup', onUp, true)      // capture phase
    return () => {
      window.removeEventListener('mousemove', onMove, true)
      window.removeEventListener('mouseup', onUp, true)
    }
  }, [])

  useEffect(() => {
    if (!annotation) return
    // For new annotations, start with empty title (no pre-fill)
    const isNewAnnotation = annotation.title === 'Neue Notiz'
    setTitle(isNewAnnotation ? '' : annotation.title)
    setTooltip(annotation.tooltip)
    setType(annotation.type)
    setShowPopup(annotation.showPopup ?? true)
    setLinkedDocIds(annotation.paperlessDocIds ?? [])
    setShowActions(false)
    setShowDocPicker(false)
    setDragPos(_lastDragPos)  // Restore last position
  }, [annotation?.id])

  // Fetch metadata for linked Paperless docs
  useEffect(() => {
    if (linkedDocIds.length === 0) { setLinkedDocs([]); return }
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetchDocuments({ page_size: 50 })
        if (cancelled) return
        const byId = new Map(res.results.map(d => [d.id, d]))
        setLinkedDocs(linkedDocIds.map(id => byId.get(id)).filter(Boolean) as PaperlessDocument[])
      } catch { /* ignore */ }
    })()
    return () => { cancelled = true }
  }, [linkedDocIds])

  useEffect(() => {
    if (!showTypeDropdown && !showActions && !showDocPicker) return
    function handleClick(e: MouseEvent) {
      if (showTypeDropdown && typeDropdownRef.current && !typeDropdownRef.current.contains(e.target as Node))
        setShowTypeDropdown(false)
      if (showActions && actionsRef.current && !actionsRef.current.contains(e.target as Node))
        setShowActions(false)
      if (showDocPicker && docPickerRef.current && !docPickerRef.current.contains(e.target as Node))
        setShowDocPicker(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [showTypeDropdown, showActions, showDocPicker])

  const isReadOnly = mode === 'view'
  const isNew = annotation?.title === 'Neue Notiz'

  if (!annotation || mode === 'none' || mode === 'place' || mode === 'relocate') return null

  function handleSave() {
    if (!annotation) return
    updateAnnotation(annotation.id, { title: title || 'Neue Notiz', tooltip, type, showPopup, paperlessDocIds: linkedDocIds })
    closeEditor()
  }

  function handleDelete() {
    if (!annotation) return
    deleteAnnotation(annotation.id)
  }

  function handleCancel() {
    if (!annotation) return
    if (isNew && !title.trim()) {
      deleteAnnotation(annotation.id)
    } else {
      closeEditor()
    }
  }

  function handleDuplicate() {
    if (!annotation) return
    const dup = duplicateAnnotation(annotation.id)
    if (dup) openAnnotationEditor(dup.id)
  }

  function handleRelocate() {
    if (!annotation) return
    startRelocating(annotation.id)
  }

  function handleCaptureCamera() {
    const viewer = viewerRef.current
    if (!viewer || !annotation) return
    const cam = captureCurrentCamera(viewer)
    updateAnnotation(annotation.id, { camera: cam })
    // Thumbnail aktualisieren (Viewer zeigt die neue Perspektive)
    try { captureAndStoreAnnotationThumbnail(annotation.id) } catch { /* noop */ }
  }

  async function openDocPicker() {
    setShowDocPicker(true)
    setPickerQuery('')
    setPickerLoading(true)
    try {
      const res = await fetchDocuments({ page_size: 30, ordering: '-created' })
      setPickerDocs(res.results)
    } catch { setPickerDocs([]) }
    setPickerLoading(false)
  }

  async function searchDocs(q: string) {
    setPickerQuery(q)
    setPickerLoading(true)
    try {
      const res = await fetchDocuments({ page_size: 30, ordering: '-created', query: q || undefined })
      setPickerDocs(res.results)
    } catch { setPickerDocs([]) }
    setPickerLoading(false)
  }

  function linkDoc(doc: PaperlessDocument) {
    if (linkedDocIds.includes(doc.id)) return
    setLinkedDocIds(prev => [...prev, doc.id])
    setLinkedDocs(prev => [...prev, doc])
    setShowDocPicker(false)
  }

  function unlinkDoc(docId: number) {
    setLinkedDocIds(prev => prev.filter(id => id !== docId))
    setLinkedDocs(prev => prev.filter(d => d.id !== docId))
  }

  return (
    <div
      ref={dialogRef}
      data-component="AnnotationEditor"
      data-mode={mode}
      data-annotation-id={annotation.id}
      style={{
        position: 'fixed',
        ...(dragPos
          ? { top: dragPos.y, left: dragPos.x, transform: 'none' }
          : { top: '1.5em', right: 'calc((100vw - var(--shell-offset, 0px)) / 3)' }
        ),
        width: '26.25em',   /* 420px at 16px */
        maxHeight: '85vh',
        display: 'flex',
        flexDirection: 'column',
        background: '#ffffff',
        borderRadius: '0.75em',  /* 12px at 16px */
        border: `1px solid ${C.border}`,
        boxShadow: '0 0.75em 2.5em rgba(0, 0, 0, 0.12), 0 0.25em 0.75em rgba(0, 0, 0, 0.06)',
        fontFamily: C.fontSans,
        color: C.textPrimary,
        zIndex: 20000,
        pointerEvents: 'auto',
        overflow: 'hidden',
      }}
    >
      {/* ─── Header (like audit dialog) ─────────────────────────── */}
      <div
        data-section="note-header"
        onMouseDown={handleDragStart}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0.625em 1em',
          borderBottom: `1px solid ${C.border}`,
          background: '#fafafa',
          cursor: 'grab',
          userSelect: 'none',
          flexShrink: 0,
        }}
      >
        <span style={{
          fontSize: '0.9375em',
          fontWeight: 700,
          color: C.textPrimary,
          display: 'flex',
          alignItems: 'center',
          gap: '0.375em',
          letterSpacing: '-0.01em',
        }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
          </svg>
          {isNew ? 'Notiz erstellen' : 'Notiz bearbeiten'}
        </span>
        <button
          data-element="btn-close"
          onClick={handleCancel}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: '2em', height: '2em',
            borderRadius: '0.375em',
            border: 'none', background: 'transparent', color: '#9ca3af',
            fontSize: '1em',
            cursor: 'pointer', flexShrink: 0,
          }}
          onMouseEnter={e => { e.currentTarget.style.background = C.hoverBg; e.currentTarget.style.color = C.textPrimary }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#9ca3af' }}
        >
          ×
        </button>
      </div>

      {/* ─── Scrollable Body ──────────────────────────────────────── */}
      <div data-section="editor-body" style={{ overflowY: 'auto', flex: 1, padding: '1em 1.25em' }}>

        {/* Title + Type Row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5em', marginBottom: '0.5em' }}>
          {/* Title */}
          <input
            data-element="input-title"
            value={title}
            onChange={e => setTitle(e.target.value)}
            readOnly={isReadOnly}
            placeholder="Titel"
            style={{
              flex: 1, boxSizing: 'border-box',
              padding: '0.7692em 0.9231em',
              borderRadius: '0.5em',
              border: 'none', background: '#f3f4f6',
              color: C.textPrimary,
              fontSize: '0.8125em',
              fontFamily: C.fontSans,
              outline: 'none',
            }}
            onFocus={e => { e.currentTarget.style.background = '#eef0f2' }}
            onBlur={e => { e.currentTarget.style.background = '#f3f4f6' }}
          />

          {/* Type selector — right-aligned with color */}
          <div ref={typeDropdownRef} style={{ position: 'relative', flexShrink: 0 }}>
            <button
              data-element="type-trigger"
              onClick={() => !isReadOnly && setShowTypeDropdown(!showTypeDropdown)}
              disabled={isReadOnly}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.375em',
                padding: '0.5em 0.625em',
                borderRadius: '0.5em',
                border: `1px solid ${C.border}`,
                background: '#ffffff',
                cursor: isReadOnly ? 'default' : 'pointer', fontFamily: C.fontSans,
              }}
              onMouseEnter={e => { if (!isReadOnly) e.currentTarget.style.background = '#f9fafb' }}
              onMouseLeave={e => { e.currentTarget.style.background = '#ffffff' }}
            >
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '1em', height: '1em' }}>
                <TypeMarker color={ANNOTATION_TYPE_COLORS[type]} size={16} />
              </span>
              <span style={{
                fontSize: '0.75em', fontWeight: 500,
                color: ANNOTATION_TYPE_COLORS[type],
                whiteSpace: 'nowrap',
              }}>
                {ANNOTATION_TYPE_LABELS[type]}
              </span>
              {!isReadOnly && (
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none" style={{ opacity: 0.4 }}>
                  <path d="M3 5L6 8L9 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>

            {showTypeDropdown && (
              <div data-section="type-dropdown" style={{
                position: 'absolute', top: '100%', right: 0, marginTop: '0.25em',
                minWidth: '14em',
                background: '#ffffff', border: `1px solid ${C.border}`,
                borderRadius: '0.5em', boxShadow: C.shadowLg, padding: '0.25em', zIndex: 10,
              }}>
                {ANNOTATION_TYPES.map(t => {
                  const col = ANNOTATION_TYPE_COLORS[t]
                  return (
                    <button
                      key={t}
                      onClick={() => { setType(t); setShowTypeDropdown(false) }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.5em',
                        width: '100%',
                        padding: '0.5em 0.625em',
                        borderRadius: '0.375em',
                        border: 'none',
                        background: t === type ? col + '12' : 'transparent',
                        color: C.textPrimary,
                        fontSize: '0.75em',
                        fontWeight: t === type ? 600 : 400,
                        cursor: 'pointer', fontFamily: C.fontSans, textAlign: 'left',
                      }}
                      onMouseEnter={e => { if (t !== type) e.currentTarget.style.background = C.hoverBg }}
                      onMouseLeave={e => { if (t !== type) e.currentTarget.style.background = t === type ? col + '12' : 'transparent' }}
                    >
                      <span style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        width: '1.5em', height: '1.5em',
                        flexShrink: 0,
                      }}>
                        <TypeMarker color={col} size={18} />
                      </span>
                      <span style={{ color: t === type ? col : C.textPrimary }}>
                        {ANNOTATION_TYPE_LABELS[t]}
                      </span>
                      {t === type && (
                        <span style={{ marginLeft: 'auto', color: col, fontSize: '1.1em' }}>✓</span>
                      )}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        {/* Short Info */}
        <textarea
          data-element="input-tooltip"
          value={tooltip}
          onChange={e => setTooltip(e.target.value)}
          readOnly={isReadOnly}
          placeholder="Short Info"
          rows={3}
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: '0.7692em 0.9231em',
            borderRadius: '0.5em',
            border: 'none', background: '#f3f4f6',
            color: C.textPrimary,
            fontSize: '0.8125em',
            fontFamily: C.fontSans,
            outline: 'none', resize: 'vertical', lineHeight: '1.5',
            marginBottom: '0.75em', /* 12px/16px */
          }}
          onFocus={e => { e.currentTarget.style.background = '#eef0f2' }}
          onBlur={e => { e.currentTarget.style.background = '#f3f4f6' }}
        />

        {/* Checkboxes */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5em', marginBottom: '1em' }}>
          {/* 8px/16px gap, 16px/16px mb */}
          <label style={{
            display: 'flex', alignItems: 'center', gap: '0.5em',
            cursor: isReadOnly ? 'default' : 'pointer',
            fontSize: '0.8125em', color: C.textPrimary, userSelect: 'none',
          }}>
            <input
              type="checkbox" checked={showPopup}
              onChange={e => setShowPopup(e.target.checked)}
              disabled={isReadOnly}
              style={{ width: '1em', height: '1em', /* 16px/16px */
                accentColor: C.primary, cursor: isReadOnly ? 'default' : 'pointer', margin: 0 }}
            />
            Popup anzeigen
          </label>
        </div>

        {/* ─── Verknüpfte Paperless-Dokumente ──────────────────────── */}
        <div data-section="linked-docs" style={{ marginBottom: '0.75em' }}>
          {/* Linked doc cards */}
          {linkedDocs.map(doc => (
            <div
              key={doc.id}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.5em',
                padding: '0.375em 0.5em',
                borderRadius: '0.375em',
                border: `1px solid ${C.border}`,
                background: '#fafafa',
                marginBottom: '0.375em',
              }}
            >
              <img
                src={getDocumentThumbnailUrl(doc.id)}
                alt=""
                style={{
                  width: '2em', height: '2em',
                  borderRadius: '0.25em',
                  objectFit: 'cover',
                  background: '#e5e7eb',
                  flexShrink: 0,
                }}
                onError={e => { (e.target as HTMLImageElement).style.display = 'none' }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: '0.75em', fontWeight: 600, color: C.textPrimary,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {doc.title}
                </div>
                <div style={{
                  fontSize: '0.625em', color: C.textDim,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {doc.original_file_name}
                </div>
              </div>
              {/* Open in viewer */}
              <button
                title="Dokument öffnen"
                onClick={() => {
                  if ((window as any).showDocViewer) {
                    (window as any).showDocViewer({ id: doc.id, title: doc.title, fileName: doc.original_file_name, tags: [], created: doc.created, pageCount: null, docTypeName: null })
                  }
                }}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: '1.5em', height: '1.5em', padding: 0,
                  border: 'none', background: 'none', cursor: 'pointer', color: C.textDim, flexShrink: 0,
                }}
                onMouseEnter={e => { e.currentTarget.style.color = C.textPrimary }}
                onMouseLeave={e => { e.currentTarget.style.color = C.textDim }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                </svg>
              </button>
              {/* Unlink */}
              {!isReadOnly && (
                <button
                  title="Verknüpfung entfernen"
                  onClick={() => unlinkDoc(doc.id)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: '1.5em', height: '1.5em', padding: 0,
                    border: 'none', background: 'none', cursor: 'pointer', color: C.textDim, flexShrink: 0,
                  }}
                  onMouseEnter={e => { e.currentTarget.style.color = C.danger }}
                  onMouseLeave={e => { e.currentTarget.style.color = C.textDim }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              )}
            </div>
          ))}

          {/* Add doc button / Picker */}
          {!isReadOnly && (
            <div ref={docPickerRef} style={{ position: 'relative' }}>
              <button
                onClick={openDocPicker}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.375em',
                  width: '100%', padding: '0.5em 0.75em',
                  borderRadius: '0.375em',
                  border: `1.5px dashed ${C.border}`,
                  background: '#fafafa',
                  cursor: 'pointer',
                  fontFamily: C.fontSans,
                  fontSize: '0.6875em',
                  color: C.textDim,
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = C.primary; e.currentTarget.style.color = C.primary }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.textDim }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                Paperless-Dokument verknüpfen
              </button>

              {/* Picker dropdown */}
              {showDocPicker && (
                <div
                  data-section="doc-picker"
                  style={{
                    position: 'absolute', bottom: '100%', left: 0, right: 0,
                    marginBottom: '0.25em',
                    maxHeight: '18em',
                    display: 'flex', flexDirection: 'column',
                    background: '#ffffff',
                    border: `1px solid ${C.border}`,
                    borderRadius: '0.5em',
                    boxShadow: C.shadowLg,
                    zIndex: 20,
                    overflow: 'hidden',
                  }}
                >
                  {/* Search */}
                  <div style={{ padding: '0.5em', borderBottom: `1px solid ${C.border}` }}>
                    <input
                      autoFocus
                      value={pickerQuery}
                      onChange={e => searchDocs(e.target.value)}
                      placeholder="Dokument suchen…"
                      style={{
                        width: '100%', boxSizing: 'border-box',
                        padding: '0.5em 0.625em',
                        borderRadius: '0.375em',
                        border: `1px solid ${C.border}`,
                        background: '#f9fafb',
                        fontSize: '0.75em',
                        fontFamily: C.fontSans,
                        color: C.textPrimary,
                        outline: 'none',
                      }}
                      onFocus={e => { e.currentTarget.style.borderColor = C.primary }}
                      onBlur={e => { e.currentTarget.style.borderColor = C.border }}
                    />
                  </div>

                  {/* Results */}
                  <div style={{ overflowY: 'auto', flex: 1 }}>
                    {pickerLoading && (
                      <div style={{ padding: '1em', textAlign: 'center', fontSize: '0.6875em', color: C.textDim }}>
                        Laden…
                      </div>
                    )}
                    {!pickerLoading && pickerDocs.length === 0 && (
                      <div style={{ padding: '1em', textAlign: 'center', fontSize: '0.6875em', color: C.textDim }}>
                        Keine Dokumente gefunden
                      </div>
                    )}
                    {!pickerLoading && pickerDocs.map(doc => {
                      const alreadyLinked = linkedDocIds.includes(doc.id)
                      return (
                        <button
                          key={doc.id}
                          onClick={() => !alreadyLinked && linkDoc(doc)}
                          disabled={alreadyLinked}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '0.5em',
                            width: '100%', padding: '0.5em 0.625em',
                            border: 'none',
                            background: alreadyLinked ? '#f3f4f6' : 'transparent',
                            cursor: alreadyLinked ? 'default' : 'pointer',
                            fontFamily: C.fontSans, textAlign: 'left',
                            opacity: alreadyLinked ? 0.5 : 1,
                          }}
                          onMouseEnter={e => { if (!alreadyLinked) e.currentTarget.style.background = C.hoverBg }}
                          onMouseLeave={e => { if (!alreadyLinked) e.currentTarget.style.background = alreadyLinked ? '#f3f4f6' : 'transparent' }}
                        >
                          <img
                            src={getDocumentThumbnailUrl(doc.id)}
                            alt=""
                            style={{
                              width: '1.75em', height: '1.75em',
                              borderRadius: '0.1875em',
                              objectFit: 'cover',
                              background: '#e5e7eb',
                              flexShrink: 0,
                            }}
                            onError={e => { (e.target as HTMLImageElement).style.display = 'none' }}
                          />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              fontSize: '0.75em', fontWeight: 500, color: C.textPrimary,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>
                              {doc.title}
                            </div>
                            <div style={{
                              fontSize: '0.5625em', color: C.textDim,
                            }}>
                              {new Date(doc.created).toLocaleDateString('de-DE')}
                            </div>
                          </div>
                          {alreadyLinked && (
                            <span style={{ fontSize: '0.625em', color: C.textDim }}>verknüpft</span>
                          )}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Read-only: show empty state if no docs */}
          {isReadOnly && linkedDocs.length === 0 && (
            <div style={{ fontSize: '0.6875em', color: C.textDim, fontStyle: 'italic' }}>
              Keine Dokumente verknüpft
            </div>
          )}
        </div>

        {/* ─── Aktionen ──────────────────────────────────────────── */}
        {!isNew && (
          <div data-section="actions" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375em', marginBottom: '0.5em' }}>
            {/* Verschieben */}
            <button
              data-element="btn-relocate"
              onClick={handleRelocate}
              title="Position in der Punktwolke ändern"
              style={actionBtnBase}
              onMouseEnter={e => { e.currentTarget.style.background = '#f9fafb' }}
              onMouseLeave={e => { e.currentTarget.style.background = '#ffffff' }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="5 9 2 12 5 15"/><polyline points="9 5 12 2 15 5"/>
                <polyline points="15 19 12 22 9 19"/><polyline points="19 9 22 12 19 15"/>
                <line x1="2" y1="12" x2="22" y2="12"/><line x1="12" y1="2" x2="12" y2="22"/>
              </svg>
            </button>

            {/* Duplizieren */}
            <button
              data-element="btn-duplicate"
              onClick={handleDuplicate}
              title="Notiz kopieren"
              style={actionBtnBase}
              onMouseEnter={e => { e.currentTarget.style.background = '#f9fafb' }}
              onMouseLeave={e => { e.currentTarget.style.background = '#ffffff' }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
              </svg>
            </button>

            {/* Kamera übernehmen */}
            <button
              data-element="btn-capture-camera"
              onClick={handleCaptureCamera}
              title="Aktuelle Kameraposition für diese Notiz speichern"
              style={actionBtnBase}
              onMouseEnter={e => { e.currentTarget.style.background = '#f9fafb' }}
              onMouseLeave={e => { e.currentTarget.style.background = '#ffffff' }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/>
                <circle cx="12" cy="13" r="3"/>
              </svg>
            </button>

            {/* Löschen */}
            <button
              data-element="btn-delete"
              onClick={handleDelete}
              title="Notiz löschen"
              style={dangerBtnBase}
              onMouseEnter={e => { e.currentTarget.style.background = C.dangerLight }}
              onMouseLeave={e => { e.currentTarget.style.background = '#ffffff' }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* ─── Footer ───────────────────────────────────────────────── */}
      <div
        data-section="editor-footer"
        style={{
          display: 'flex', justifyContent: 'flex-end', gap: '0.5em',
          padding: '0.75em 1.25em 1em', /* 12px/16px, 20px/16px, 16px/16px */
          flexShrink: 0, borderTop: `1px solid ${C.border}`,
        }}
      >
        {isReadOnly ? (
          <>
            <button
              onClick={() => openAnnotationEditor(annotation.id)}
              style={{
                padding: '0.5em 1.25em',  /* 8px/16px, 20px/16px */
                borderRadius: '0.5em',
                border: `1px solid ${C.border}`,
                background: '#ffffff', color: C.textPrimary,
                fontSize: '0.8125em', fontWeight: 500,
                cursor: 'pointer', fontFamily: C.fontSans,
              }}
            >
              Bearbeiten
            </button>
            <button
              onClick={closeEditor}
              style={{
                padding: '0.5em 1.25em',
                borderRadius: '0.5em',
                border: 'none',
                background: '#111827', color: '#ffffff',
                fontSize: '0.8125em', fontWeight: 600,
                cursor: 'pointer', fontFamily: C.fontSans,
              }}
            >
              Schließen
            </button>
          </>
        ) : (
          <>
            <button
              onClick={handleCancel}
              style={{
                padding: '0.5em 1.25em',
                borderRadius: '0.5em',
                border: `1px solid ${C.border}`,
                background: '#ffffff', color: C.textPrimary,
                fontSize: '0.8125em', fontWeight: 500,
                cursor: 'pointer', fontFamily: C.fontSans,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#f9fafb' }}
              onMouseLeave={e => { e.currentTarget.style.background = '#ffffff' }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              style={{
                padding: '0.5em 1.5em', /* 8px/16px, 24px/16px */
                borderRadius: '0.5em',
                border: 'none',
                background: '#111827', color: '#ffffff',
                fontSize: '0.8125em', fontWeight: 600,
                cursor: 'pointer', fontFamily: C.fontSans,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#1f2937' }}
              onMouseLeave={e => { e.currentTarget.style.background = '#111827' }}
            >
              {isNew ? 'Create' : 'Save'}
            </button>
          </>
        )}
      </div>
    </div>
  )
}
